import React, { useEffect, useMemo, useState } from 'react';
import SupportSearchBar from './SupportSearchBar';
import ArticleList from './ArticleList';
import ArticleReaderModal from './ArticleReaderModal';
import TicketList from './TicketList';
import TicketDrawer from './TicketDrawer';
import NewTicketWizardModal from './NewTicketWizardModal';
import {
  fetchSupportArticles,
  fetchSystemStatus,
  seedSupportArticles,
  submitArticleFeedback,
} from '../../lib/support/articlesService';
import { listUserTickets } from '../../lib/support/ticketsService';
import './support.css';

const QUICK_TILES = [
  { title: 'How to connect Google Drive', tag: 'drive' },
  { title: 'Templates: create and export', tag: 'templates' },
  { title: 'Exclusive Mandates: add projects', tag: 'mandates' },
  { title: 'Billing & invoices', tag: 'billing' },
  { title: 'Account & login', tag: 'login' },
];

const CATEGORIES = [
  'Getting Started',
  'Branding & Marketing Suite',
  'Templates',
  'Exclusive Mandates',
  'Events',
  'Account & Billing',
  'Troubleshooting',
];

const SupportPage = ({ firebase, authUser, showAlert }) => {
  const [query, setQuery] = useState('');
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const isAdminUser = authUser?.email === 'jeethbuildcon@gmail.com';

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      try {
        if (isAdminUser) {
          await seedSupportArticles(firebase);
        }
      } catch (error) {
        showAlert?.({
          title: 'Support seed failed',
          message: error?.message || 'Unable to seed support articles',
          variant: 'error',
        });
      }
      try {
        const data = await fetchSupportArticles(firebase);
        if (active) setArticles(data);
      } catch (error) {
        showAlert?.({
          title: 'Support articles unavailable',
          message: error?.message || 'Unable to load help articles',
          variant: 'error',
        });
      }
      try {
        const status = await fetchSystemStatus(firebase);
        if (active) setStatusData(status);
      } catch (error) {
        showAlert?.({
          title: 'Status unavailable',
          message: error?.message || 'Unable to load system status',
          variant: 'error',
        });
      }
    };
    loadData();
    return () => {
      active = false;
    };
  }, [firebase, isAdminUser, showAlert]);

  useEffect(() => {
    if (!authUser?.uid) return undefined;
    return listUserTickets(firebase, authUser.uid, setTickets);
  }, [firebase, authUser]);

  const suggestions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return [];
    return articles
      .filter((article) =>
        article.title?.toLowerCase().includes(trimmed) ||
        article.tags?.some((tag) => tag.includes(trimmed)) ||
        article.category?.toLowerCase().includes(trimmed),
      )
      .slice(0, 6);
  }, [articles, query]);

  const searchResults = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (trimmed.length < 2) return [];
    return articles.filter((article) => {
      const haystack = `${article.title} ${article.category} ${(article.tags || []).join(' ')}`.toLowerCase();
      return trimmed.split(/\s+/).every((token) => haystack.includes(token));
    });
  }, [articles, query]);

  const handleArticleFeedback = async (helpful, comment) => {
    if (!selectedArticle) return;
    try {
      await submitArticleFeedback(firebase, {
        articleId: selectedArticle.id,
        uid: authUser?.uid || null,
        helpful,
        comment: comment || '',
      });
      showAlert?.({
        title: 'Thanks for the feedback',
        message: helpful ? 'Glad it helped!' : 'We will improve this article.',
        variant: 'success',
      });
    } catch (error) {
      showAlert?.({
        title: 'Feedback failed',
        message: error?.message || 'Unable to send feedback',
        variant: 'error',
      });
    }
  };

  const groupedResults = useMemo(() => {
    const groups = {};
    searchResults.forEach((article) => {
      const key = article.category || 'Other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(article);
    });
    return groups;
  }, [searchResults]);

  return (
    <div>
      <div className="support-header">
        <div>
          <h1>Support</h1>
          <p>Search help articles, raise a ticket, or talk to us.</p>
        </div>
        <div className="support-header-actions">
          <button className="support-btn primary" onClick={() => setShowTicketModal(true)}>New Ticket</button>
          <button className="support-btn" onClick={() => showAlert?.({ title: 'Chat', message: 'Chat coming soon.', variant: 'info' })}>Chat with Support</button>
          <button className="support-btn" onClick={() => showAlert?.({ title: 'Status', message: 'System status updated live.', variant: 'info' })}>System Status</button>
        </div>
      </div>

      <div className="support-page">
        <div>
          <div className="support-card">
            <div className="support-section-title">Help Center</div>
            <SupportSearchBar
              query={query}
              onQueryChange={setQuery}
              suggestions={suggestions}
              onSelectSuggestion={(article) => setSelectedArticle(article)}
              onSubmit={() => null}
            />
            {query.trim().length > 0 ? (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ color: '#bdbdbd', fontSize: '0.85rem' }}>Results for: <strong>{query}</strong></div>
                {query.trim().length < 2 ? (
                  <div className="support-empty">Type at least 2 characters to search.</div>
                ) : searchResults.length === 0 ? (
                  <div className="support-empty">No results found.</div>
                ) : (
                  Object.entries(groupedResults).map(([category, items]) => (
                    <ArticleList key={category} title={category} items={items} onSelect={setSelectedArticle} />
                  ))
                )}
              </div>
            ) : (
              <>
                <div className="support-quick-tiles">
                  {QUICK_TILES.map((tile) => (
                    <div
                      key={tile.title}
                      className="support-tile"
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        const match = articles.find((article) => article.title === tile.title || article.tags?.includes(tile.tag));
                        if (match) setSelectedArticle(match);
                      }}
                      onKeyDown={(event) => event.key === 'Enter' && setSelectedArticle(articles[0])}
                    >
                      <div style={{ fontWeight: 600 }}>{tile.title}</div>
                      <div style={{ fontSize: '0.8rem', color: '#9b9b9b' }}>Popular guide</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: '1.5rem' }}>
                  <div className="support-section-title">Categories</div>
                  <div className="support-categories">
                    {CATEGORIES.map((category) => (
                      <div key={category} className="support-category">{category}</div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div>
          <div className="support-card">
            <div className="support-section-title">My Tickets</div>
            <TicketList tickets={tickets} onSelect={setSelectedTicket} />
            <button className="support-btn primary" style={{ marginTop: '1rem', width: '100%' }} onClick={() => setShowTicketModal(true)}>
              New Ticket
            </button>
          </div>
          <div className="support-card support-status-panel">
            <div className="support-section-title">System Status</div>
            {statusData?.components?.length ? (
              statusData.components.map((component) => (
                <div key={component.name} className="support-status-item">
                  <span>{component.name}</span>
                  <span>{component.status}</span>
                </div>
              ))
            ) : (
              <div className="support-empty">All systems operational.</div>
            )}
          </div>
        </div>
      </div>

      {selectedArticle && (
        <ArticleReaderModal
          article={selectedArticle}
          onClose={() => setSelectedArticle(null)}
          onFeedback={handleArticleFeedback}
        />
      )}
      {selectedTicket && (
        <TicketDrawer
          firebase={firebase}
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
        />
      )}
      {showTicketModal && (
        <NewTicketWizardModal
          firebase={firebase}
          authUser={authUser}
          onClose={() => setShowTicketModal(false)}
          onCreated={(ticket) => {
            setTickets((prev) => [ticket, ...prev]);
            showAlert?.({
              title: 'Ticket created',
              message: `We received ${ticket.ticketNumber}. We'll respond soon.`,
              variant: 'success',
            });
          }}
        />
      )}
    </div>
  );
};

export default SupportPage;
